import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Rnd } from 'react-rnd'
import { LayoutGrid, Trash2, X, Download, Upload, CalendarPlus, Maximize, Minimize } from 'lucide-react'
import { useTaskStore } from '../../store/taskStore'
import { WIDGET_REGISTRY } from './widgetRegistry'
import { WidgetType } from '../../types'
import clsx from 'clsx'
import { downloadIcs, tasksFromCsv, tasksFromIcs, tasksFromMicrosoftJson } from '../../services/taskTransfer'
import FocusMode from '../Focus/FocusMode'
import ZenMode from '../Focus/ZenMode'
import { isFullscreen, toggleFullscreen } from '../../services/fullscreen'

export default function FloatingMenu() {
  const [fullscreen, setFullscreen] = useState(false)
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { addCanvasWidget, removeCanvasWidget, clearCanvas, floatingMenuState, updateFloatingMenuState, exportData, importData, importTasks, tasks, pages, currentPageId } = useTaskStore()

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen())
    document.addEventListener('fullscreenchange', sync)
    sync()
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  const currentPage = pages.find((p) => p.id === currentPageId)
  const canvasWidgets = currentPage?.canvasWidgets ?? []
  const activeTypes = new Set(canvasWidgets.map((w: any) => w.type))

  const viewportPadding = 12
  const minMenuHeight = 300
  const menuWidth = Math.min(floatingMenuState.width, Math.max(280, window.innerWidth - viewportPadding * 2))
  const menuX = Math.max(
    viewportPadding,
    Math.min(floatingMenuState.x, window.innerWidth - menuWidth - viewportPadding),
  )
  const menuY = Math.max(
    viewportPadding,
    Math.min(floatingMenuState.y, Math.max(viewportPadding, window.innerHeight - minMenuHeight - viewportPadding)),
  )
  const menuHeight = Math.max(minMenuHeight, window.innerHeight - menuY - viewportPadding)

  const toggle = (type: WidgetType) => {
    const existing = canvasWidgets.filter((w: any) => w.type === type)
    if (existing.length > 0) {
      existing.forEach((w: any) => removeCanvasWidget(w.instanceId))
    } else {
      const offset = canvasWidgets.length * 24
      addCanvasWidget(type, 100 + (offset % 300), 80 + (offset % 200))
    }
  }

  const handleExport = () => {
    const json = exportData()
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `task-priority-backup-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      try {
        const extension = file.name.split('.').pop()?.toLowerCase()
        if (extension === 'json') {
          const parsed = JSON.parse(content)
          if (parsed && !Array.isArray(parsed) && (parsed.pages || parsed.dashboardLayout || parsed.projects)) {
            const success = importData(content)
            setMessage(success ? { text: 'Backup importado com sucesso!' } : { text: 'Não foi possível importar o backup.', error: true })
          } else {
            const count = importTasks(tasksFromMicrosoftJson(content))
            setMessage({ text: `${count} tarefa${count === 1 ? '' : 's'} da Microsoft importada${count === 1 ? '' : 's'}.` })
          }
        } else {
          const imported = extension === 'ics' ? tasksFromIcs(content) : tasksFromCsv(content)
          const count = importTasks(imported)
          setMessage({ text: `${count} tarefa${count === 1 ? '' : 's'} importada${count === 1 ? '' : 's'}.` })
        }
      } catch {
        setMessage({ text: 'Erro ao importar. Use um backup JSON, CSV/ICS do Outlook ou JSON do Microsoft Graph.', error: true })
      }
    }
    reader.readAsText(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return createPortal((
    <>
      {message && <div role={message.error ? 'alert' : 'status'} className={clsx('fixed bottom-24 right-6 z-[100001] rounded-xl px-4 py-3 text-sm shadow-xl', message.error ? 'bg-red-700 text-white' : 'bg-slate-900 text-white')} onClick={() => setMessage(null)}>{message.text}</div>}
      {floatingMenuState.open && (
        <div className="fixed inset-0" style={{ zIndex: 99998 }} onClick={() => updateFloatingMenuState({ open: false })} />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.csv,.ics,text/calendar,text/csv,application/json"
        onChange={handleImportFile}
        className="hidden"
      />

      {floatingMenuState.open && (
        <Rnd
          default={{
            x: menuX,
            y: menuY,
            width: menuWidth,
            height: menuHeight,
          }}
          onDragStop={(e, d) => {
            updateFloatingMenuState({ x: d.x, y: d.y })
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            updateFloatingMenuState({
              x: position.x,
              y: position.y,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
            })
          }}
          minWidth={280}
          minHeight={300}
          bounds="window"
          dragHandleClassName="widget-drag-handle"
          style={{ zIndex: 99999 }}
        >
        <div className="h-full flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden text-slate-800 dark:text-slate-100 transition-colors">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80">
            <div className="widget-drag-handle flex-1 cursor-move">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Painel de Widgets</span>
            </div>
            <button
              onClick={() => updateFloatingMenuState({ open: false })}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex-shrink-0 ml-2"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
            {/* Export / Import buttons */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={handleExport}
                className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 rounded-xl py-2 text-xs font-semibold transition-colors"
              >
                <Download size={13} /> Exportar
              </button>
              <button
                onClick={handleImportClick}
                className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-xl py-2 text-xs font-semibold transition-colors"
              >
                <Upload size={13} /> Importar
              </button>
            </div>
            <button
              onClick={() => {
                const scheduled = tasks.filter((task) => task.status !== 'done' && (task.startDate || task.dueDate))
                if (!scheduled.length) return setMessage({ text: 'Adicione uma data a pelo menos uma tarefa para exportar a agenda.', error: true })
                downloadIcs(scheduled)
                setMessage({ text: `${scheduled.length} tarefa${scheduled.length === 1 ? '' : 's'} exportada${scheduled.length === 1 ? '' : 's'} para a agenda.` })
              }}
              className="mb-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-violet-200 dark:border-violet-800/60 bg-violet-50 dark:bg-violet-950/40 py-2 text-xs font-semibold text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/60 transition-colors"
            >
              <CalendarPlus size={13} /> Exportar agenda (.ics)
            </button>

            {/* Widgets toggles */}
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto custom-scrollbar">
              {WIDGET_REGISTRY.map(({ type, label, Icon, color }) => {
                const active = activeTypes.has(type)
                return (
                  <button
                    key={type}
                    onClick={() => toggle(type)}
                    className={clsx(
                      'w-full flex items-center gap-2.5 p-2.5 rounded-xl border text-left transition-all text-xs',
                      active
                        ? 'border-blue-500 bg-blue-50/80 dark:bg-blue-950/50 dark:border-blue-700 shadow-sm'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    )}
                  >
                    <div className={clsx('p-1.5 rounded-lg shrink-0 shadow-sm', color)}>
                      <Icon size={12} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 dark:text-slate-200 truncate">{label}</p>
                      <p className={clsx('text-[11px]', active ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-slate-400 dark:text-slate-500')}>
                        {active ? 'Visível' : 'Oculto'}
                      </p>
                    </div>
                    <div
                      className={clsx(
                        'w-3 h-3 rounded-full border-2 shrink-0 transition-colors',
                        active ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500' : 'border-slate-300 dark:border-slate-600'
                      )}
                    />
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => { clearCanvas(); updateFloatingMenuState({ open: false }) }}
              className="mt-3 w-full shrink-0 text-xs text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-700 flex items-center justify-center gap-1.5 rounded-xl border border-red-100 dark:border-red-900/40 px-2 py-2 transition-colors font-medium"
            >
              <Trash2 size={13} /> Limpar tudo
            </button>
          </div>
        </div>
        </Rnd>
      )}

      {/* Floating Speed Dial Menu - 4 Circular Buttons Popping Out From Behind FAB */}
      <div className="group fixed bottom-6 right-6 z-[100000] flex flex-col-reverse items-center select-none">
        {/* Main FAB Button (z-20 layer) */}
        <button
          onClick={() => updateFloatingMenuState({ open: !floatingMenuState.open })}
          className={clsx(
            'relative z-20 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 focus-visible:ring-4 focus-visible:ring-blue-500/40 focus-visible:outline-none',
            floatingMenuState.open
              ? 'bg-slate-900 dark:bg-slate-800 text-white rotate-45 scale-110 shadow-slate-900/40 border border-slate-700'
              : 'bg-blue-600 hover:bg-blue-500 text-white hover:scale-105 shadow-blue-600/30 border border-blue-400/30'
          )}
          title={floatingMenuState.open ? 'Fechar menu' : 'Menu de Widgets'}
          aria-label={floatingMenuState.open ? 'Fechar menu' : 'Menu de Widgets'}
        >
          {floatingMenuState.open ? <X size={22} className="text-white" /> : <LayoutGrid size={22} className="text-white" />}
        </button>

        {/* 4 Circular Action Buttons (z-10 layer, slide up from behind FAB on hover) */}
        {!floatingMenuState.open && (
          <div className="relative z-10 flex flex-col-reverse items-center gap-3.5 pb-4 px-2">
            {/* Button 1: Exportar Backup (Closest above FAB) */}
            <div className="group/item relative flex items-center transition-all duration-300 ease-out transform translate-y-12 scale-75 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto delay-[0ms]">
              <span className="absolute right-14 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 whitespace-nowrap opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none">
                Exportar Backup
              </span>
              <button
                type="button"
                onClick={handleExport}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white shadow-xl shadow-emerald-600/30 hover:bg-emerald-500 hover:scale-110 active:scale-95 transition-all duration-200 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:outline-none"
                title="Exportar Backup"
                aria-label="Exportar Backup"
              >
                <Download size={20} className="text-white" />
              </button>
            </div>

            {/* Button 2: Tela Cheia */}
            <div className="group/item relative flex items-center transition-all duration-300 ease-out transform translate-y-20 scale-75 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto delay-[50ms]">
              <span className="absolute right-14 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 whitespace-nowrap opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none">
                {fullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
              </span>
              <button
                type="button"
                onClick={() => void toggleFullscreen()}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800 dark:bg-slate-700 text-white shadow-xl hover:bg-slate-700 dark:hover:bg-slate-600 hover:scale-110 active:scale-95 transition-all duration-200 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
                title={fullscreen ? 'Sair da Tela Cheia' : 'Entrar em Tela Cheia'}
                aria-label={fullscreen ? 'Sair da Tela Cheia' : 'Entrar em Tela Cheia'}
              >
                {fullscreen ? <Minimize size={20} className="text-white" /> : <Maximize size={20} className="text-white" />}
              </button>
            </div>

            {/* Button 3: Modo Zen */}
            <div className="group/item relative flex items-center transition-all duration-300 ease-out transform translate-y-28 scale-75 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto delay-[100ms]">
              <span className="absolute right-14 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 whitespace-nowrap opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none">
                Modo Zen
              </span>
              <ZenMode iconOnly />
            </div>

            {/* Button 4: Modo Foco (Topmost) */}
            <div className="group/item relative flex items-center transition-all duration-300 ease-out transform translate-y-36 scale-75 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto delay-[150ms]">
              <span className="absolute right-14 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-lg shadow-lg border border-slate-200 dark:border-slate-800 whitespace-nowrap opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none">
                Modo Foco
              </span>
              <FocusMode iconOnly />
            </div>
          </div>
        )}
      </div>
    </>
  ), document.body)
}
